/**
 * ComingSoon — a generic, reusable "this page isn't ready yet" screen.
 *
 * Use this to temporarily hide ANY single page from the public without
 * removing it from the codebase. Drop it into App.js as the element for
 * the route you want to gate:
 *
 *     <Route path="/impact-report"
 *            element={<ComingSoon title="Impact report"
 *                                 body="Landing next week — bookmark us." />} />
 *
 * How this differs from StudentsComingSoon:
 *   - StudentsComingSoon is a *product gate* — it collects student emails
 *     for the next cohort intake. It has a form, backend, and specific copy.
 *   - ComingSoon (this file) is a *utility page* — no form, no backend, just
 *     warm placeholder copy + an escape hatch back to the rest of the site.
 *
 * All props are optional — defaults produce a reasonable generic screen.
 * Test IDs are prefixed with `coming-soon-` so QA can drive it deterministically.
 *
 * A `noindex, nofollow` meta tag is mounted automatically so any hidden
 * URL doesn't get crawled while it's parked here.
 */

import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Home as HomeIcon } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { FadeUp } from "../components/Reveal";
import { PageAccent } from "../components/PageAccent";
import { useNoIndex } from "../lib/useNoIndex";

export default function ComingSoon({
  eyebrow = "Coming soon",
  title = "This corner of the site is still being written.",
  body = "We're keeping this page hidden while we get it right. Nothing's broken — just polishing the words. Try the rest of the site for now, and this'll be here waiting for you when it's ready.",
  primaryHref = "/",
  primaryLabel = "Back to home",
  onPrimaryClick = null,
  secondaryHref = null,
  secondaryLabel = null,
  testid = "coming-soon-page",
}) {
  useNoIndex();

  return (
    <main data-testid={testid} className="bg-sand">
      <Navbar onStart={() => (window.location.href = "/")} primaryLabel="Back to home" />

      <section className="mx-auto max-w-3xl px-6 pt-36 pb-24 lg:px-8 lg:pt-56">
        <FadeUp tier="sm"><PageAccent className="mb-5" /></FadeUp>

        <FadeUp>
          <span
            data-testid="coming-soon-eyebrow"
            className="inline-flex items-center rounded-full bg-ink px-3 py-1 text-[13px] font-semibold uppercase tracking-[0.22em] text-sand"
          >
            {eyebrow}
          </span>
        </FadeUp>

        <FadeUp>
          <h1
            data-testid="coming-soon-headline"
            className="mt-6 font-serif text-[3.5rem] lg:text-[5.5rem] leading-[0.98] tracking-tight text-ink"
          >
            {title}
          </h1>
        </FadeUp>

        <FadeUp>
          <p
            data-testid="coming-soon-body"
            className="mt-6 max-w-2xl text-base leading-relaxed text-olive"
          >
            {body}
          </p>
        </FadeUp>

        <FadeUp>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-col items-start gap-3 sm:flex-row sm:items-center"
          >
            {onPrimaryClick ? (
              <button
                type="button"
                onClick={onPrimaryClick}
                data-testid="coming-soon-primary"
                className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest"
              >
                <HomeIcon className="h-4 w-4" strokeWidth={1.75} />
                {primaryLabel}
              </button>
            ) : (
              <Link
                to={primaryHref}
                data-testid="coming-soon-primary"
                className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest"
              >
                <HomeIcon className="h-4 w-4" strokeWidth={1.75} />
                {primaryLabel}
              </Link>
            )}
            {secondaryHref && secondaryLabel && (
              <Link
                to={secondaryHref}
                data-testid="coming-soon-secondary"
                className="inline-flex items-center gap-2 rounded-full border-2 border-ink/15 bg-bone px-6 py-3.5 text-base font-semibold text-ink transition-colors hover:border-ink"
              >
                {secondaryLabel}
                <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
              </Link>
            )}
          </motion.div>
        </FadeUp>

        {/* Small palette-oval accent — echoes NotFound + DoodleDivider vocabulary. */}
        <FadeUp>
          <div
            aria-hidden
            data-testid="coming-soon-palette"
            className="mt-14 flex items-center gap-2"
          >
            <span className="h-1.5 w-4 rounded-full bg-tangerine" />
            <span className="h-1.5 w-4 rounded-full bg-cobalt" />
            <span className="h-1.5 w-4 rounded-full bg-grass" />
            <span className="h-1.5 w-4 rounded-full bg-blossom" />
            <span className="h-1.5 w-4 rounded-full bg-yellow ring-1 ring-inset ring-ink/20" />
          </div>
        </FadeUp>
      </section>

      <Footer
        onStart={() => (window.location.href = "/")}
        onDonate={() => (window.location.href = "/")}
        primaryLabel="Back to home"
        secondaryLabel={null}
      />
    </main>
  );
}
